import React, { useState, useEffect, useCallback } from 'react';
import apiClient from '../../lib/apiClient';
import { useToast } from '../../hooks/use-toast';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Save, Loader2, User } from 'lucide-react';

const PersonalInfoManager = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState({
    name: '',
    title: '',
    subtitle: '',
    tagline: '',
    description: '',
    location: '',
    origin: '',
    email: '',
    phone: '',
    github: '',
    linkedin: '',
    experience: '',
  });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/api/personal-info');
      if (response) {
        setData(response);
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to load personal info',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleChange = (field, value) => {
    setData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      await apiClient.put('/api/admin/personal-info', data);
      toast({
        title: 'Success',
        description: 'Personal info updated successfully',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update personal info',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="personal-info-manager">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3 mb-2">
          <User className="w-8 h-8 text-cyan-500" />
          <h1 className="text-3xl font-bold text-white">Personal Information</h1>
        </div>
        <p className="text-neutral-400">Update your bio and contact details</p>
      </div>

      {/* Form */}
      <form onSubmit={handleSave} className="space-y-6">
        <div className="bg-neutral-900 border border-neutral-800 rounded-xl p-6 space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="text-sm font-medium text-neutral-300">Name *</label>
              <Input
                value={data.name}
                onChange={(e) => handleChange('name', e.target.value)}
                className="bg-neutral-800 border-neutral-700 text-white"
                required
                data-testid="personal-info-name"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-neutral-300">Title *</label>
              <Input
                value={data.title}
                onChange={(e) => handleChange('title', e.target.value)}
                className="bg-neutral-800 border-neutral-700 text-white"
                required
                data-testid="personal-info-title"
              />
            </div>
          </div>

          {/* Subtitle */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-neutral-300">Subtitle *</label>
            <Input
              value={data.subtitle}
              onChange={(e) => handleChange('subtitle', e.target.value)}
              className="bg-neutral-800 border-neutral-700 text-white"
              required
              data-testid="personal-info-subtitle"
            />
          </div>

          {/* Tagline */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-neutral-300">Tagline *</label>
            <Input
              value={data.tagline}
              onChange={(e) => handleChange('tagline', e.target.value)}
              className="bg-neutral-800 border-neutral-700 text-white"
              required
              data-testid="personal-info-tagline"
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-neutral-300">Description *</label>
            <Textarea
              value={data.description}
              onChange={(e) => handleChange('description', e.target.value)}
              className="bg-neutral-800 border-neutral-700 text-white"
              rows={4}
              required
              data-testid="personal-info-description"
            />
          </div>

          {/* Location & Origin */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="text-sm font-medium text-neutral-300">Location *</label>
              <Input
                value={data.location}
                onChange={(e) => handleChange('location', e.target.value)}
                className="bg-neutral-800 border-neutral-700 text-white"
                required
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-neutral-300">Origin *</label>
              <Input
                value={data.origin}
                onChange={(e) => handleChange('origin', e.target.value)}
                className="bg-neutral-800 border-neutral-700 text-white"
                required
              />
            </div>
          </div>

          {/* Contact */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="text-sm font-medium text-neutral-300">Email *</label>
              <Input
                type="email"
                value={data.email}
                onChange={(e) => handleChange('email', e.target.value)}
                className="bg-neutral-800 border-neutral-700 text-white"
                required
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-neutral-300">Phone</label>
              <Input
                type="tel"
                value={data.phone || ''}
                onChange={(e) => handleChange('phone', e.target.value)}
                className="bg-neutral-800 border-neutral-700 text-white"
                placeholder="+91 1234567890"
              />
            </div>
          </div>

          {/* Social Links */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="text-sm font-medium text-neutral-300">GitHub URL *</label>
              <Input
                value={data.github}
                onChange={(e) => handleChange('github', e.target.value)}
                className="bg-neutral-800 border-neutral-700 text-white"
                required
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-neutral-300">LinkedIn URL *</label>
              <Input
                value={data.linkedin}
                onChange={(e) => handleChange('linkedin', e.target.value)}
                className="bg-neutral-800 border-neutral-700 text-white"
                required
              />
            </div>
          </div>

          {/* Experience */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-neutral-300">Years of Experience *</label>
            <Input
              value={data.experience}
              onChange={(e) => handleChange('experience', e.target.value)}
              placeholder="e.g., 2+ years"
              className="bg-neutral-800 border-neutral-700 text-white"
              required
            />
          </div>
        </div>

        {/* Save Button */}
        <Button
          type="submit"
          disabled={saving}
          className="bg-cyan-500 hover:bg-cyan-600 text-black font-medium"
          data-testid="personal-info-save"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Save Changes
            </>
          )}
        </Button>
      </form>
    </div>
  );
};

export default PersonalInfoManager;